/**
 * A message in the thread, editable in place — Step 55.
 *
 * Kai: *"edit everything after the fact, this includes private notes, replies
 * etc."*
 *
 * Three things this is careful about, all of them about honesty rather than
 * mechanics:
 *
 *  - **the consequence is shown BEFORE the edit**, not after. A public reply
 *    has already been emailed and nothing here can reach into somebody's
 *    inbox; editing changes our record and not what they received. A warning
 *    that arrives with the confirmation is a receipt;
 *  - **an edited message says so**, permanently, with the previous wording one
 *    click away. A thread that can be silently rewritten is not a record;
 *  - **the pencil only appears where the server would allow it**, because
 *    both sides read the same `messageEditError`.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { History, Pencil } from 'lucide-react';
import { canEditMessage, editConsequence } from '@itsm/shared';
import { Button } from '../../components/ui/button';
import { RichTextEditor } from '../../components/ui/richtext';
import { Modal } from '../../components/ui/modal';
import { apiFetch, apiSend } from '../../lib/api';
import { qk } from '../../lib/queryClient';
import { useDirty } from '../../lib/dirty';
import { fullTime } from '../../lib/time';

export interface ThreadMessage {
  id: string;
  kind: string;
  bodyHtml: string;
  authorId?: string | null;
  editedAt?: number | null;
  editedByName?: string | null;
}

export function MessageBody({
  ticketId,
  message,
  actor,
}: {
  ticketId: string;
  message: ThreadMessage;
  actor: { id: string; isTicketsAdmin: boolean };
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(message.bodyHtml);
  const [historyOpen, setHistoryOpen] = useState(false);

  const dirty = editing && draft !== message.bodyHtml;
  const clearDirty = useDirty(dirty);

  const save = useMutation({
    mutationFn: () =>
      apiSend(`/api/tickets/${ticketId}/messages/${message.id}`, 'PATCH', { bodyHtml: draft }),
    onSuccess: (r) => {
      if (!r.ok) return toast.error(r.error ?? 'Could not save the edit.');
      clearDirty();
      setEditing(false);
      void qc.invalidateQueries({ queryKey: qk.tickets.detail(ticketId) });
      void qc.invalidateQueries({ queryKey: qk.tickets.activity(ticketId) });
    },
  });

  const revisions = useQuery({
    queryKey: qk.tickets.revisions(ticketId, message.id),
    queryFn: () =>
      apiFetch<{
        entries: { id: string; bodyHtml: string; editedAt: number; editedByName: string | null }[];
      }>(`/api/tickets/${ticketId}/messages/${message.id}/revisions`),
    enabled: historyOpen,
  });

  const mayEdit = canEditMessage({ kind: message.kind, authorId: message.authorId ?? null }, actor);
  const consequence = editConsequence(message.kind);

  if (editing) {
    return (
      <div className="space-y-2">
        {consequence && (
          <p className="rounded-md border border-amber-500/40 bg-amber-500/10 px-2.5 py-1.5 text-xs">
            {consequence}
          </p>
        )}
        <RichTextEditor value={draft} onChange={setDraft} minHeight={90} />
        <div className="flex justify-end gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              clearDirty();
              setDraft(message.bodyHtml);
              setEditing(false);
            }}
          >
            Cancel
          </Button>
          <Button size="sm" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="group/msg">
      <div
        className="itsm-authored prose prose-sm dark:prose-invert max-w-none overflow-x-auto"
        // Sanitised on WRITE, never on render — the standing rule.
        dangerouslySetInnerHTML={{ __html: message.bodyHtml }}
      />
      <div className="mt-1 flex items-center gap-2 text-[11px] text-muted-foreground">
        {message.editedAt && (
          <button
            type="button"
            onClick={() => setHistoryOpen(true)}
            className="flex items-center gap-1 rounded underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <History className="h-3 w-3" />
            Edited {message.editedByName ? `by ${message.editedByName} ` : ''}
            {fullTime(message.editedAt)}
          </button>
        )}
        {mayEdit && (
          <button
            type="button"
            onClick={() => {
              setDraft(message.bodyHtml);
              setEditing(true);
            }}
            className="ml-auto flex items-center gap-1 rounded opacity-0 transition-opacity focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring group-hover/msg:opacity-100"
          >
            <Pencil className="h-3 w-3" /> Edit
          </button>
        )}
      </div>

      <Modal open={historyOpen} onClose={() => setHistoryOpen(false)} title="Previous versions" wide>
        {revisions.isPending && <p className="text-sm text-muted-foreground">Loading…</p>}
        {revisions.data?.entries.length === 0 && (
          <p className="text-sm text-muted-foreground">No earlier versions.</p>
        )}
        <div className="space-y-3">
          {(revisions.data?.entries ?? []).map((r) => (
            <div key={r.id} className="rounded-md border p-3">
              <p className="mb-1 text-xs text-muted-foreground">
                Before the edit {r.editedByName ? `by ${r.editedByName} ` : ''}
                {fullTime(r.editedAt)}
              </p>
              <div
                className="itsm-authored prose prose-sm dark:prose-invert max-w-none overflow-x-auto"
                dangerouslySetInnerHTML={{ __html: r.bodyHtml }}
              />
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
}
